var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { getActiveDashboard } from '../../theme';
/**
 * Parse series for drawing.
 * Multi-series charts get theme palette colors when `series[].color` is omitted
 * (`applyThemeColors`, default true for 2+ series). User-supplied colors are kept
 * when `keepColors` is true.
 */
export function parseSeries(props, fallback, options) {
    var _a;
    if (fallback === void 0) { fallback = [10, 30, 20, 50, 40, 60]; }
    if (options === void 0) { options = {}; }
    var keepColors = options.keepColors === true;
    var raw = props.series;
    var multi = Boolean(raw && raw.length > 1);
    var applyTheme = options.applyThemeColors === true || (options.applyThemeColors !== false && multi);
    var chartStops = Array.isArray(props.colorStops)
        ? props.colorStops
        : Array.isArray(props.thresholds)
            ? props.thresholds
            : undefined;
    var palette = getActiveDashboard().series;
    if (raw === null || raw === void 0 ? void 0 : raw.length) {
        return raw.map(function (s, i) {
            var _a, _b, _c, _d;
            return ({
                name: (_b = (_a = s.name) !== null && _a !== void 0 ? _a : s.label) !== null && _b !== void 0 ? _b : "Series ".concat(i + 1),
                data: (_c = s.data) !== null && _c !== void 0 ? _c : [],
                type: s.type,
                color: (keepColors && s.color) ||
                    (applyTheme ? palette[i % palette.length] : undefined) ||
                    undefined,
                colorStops: (_d = s.colorStops) !== null && _d !== void 0 ? _d : chartStops,
                yAxis: s.yAxis,
                errorY: s.errorY,
                rangeMin: s.rangeMin,
                rangeMax: s.rangeMax,
            });
        });
    }
    var data = (_a = props.data) !== null && _a !== void 0 ? _a : fallback;
    return [
        {
            name: typeof props.seriesLabel === 'string' ? props.seriesLabel : 'Series',
            data: data,
            color: applyTheme ? getActiveDashboard().chartLine : undefined,
            colorStops: chartStops,
        },
    ];
}
/** True when the original props (not a theme-baked rebuild) supplied series colors. */
export function detectUserSeriesColors(props) {
    if (props.seriesHasUserColors === true)
        return true;
    if (props.seriesHasUserColors === false)
        return false;
    // Rebuild without a persisted flag → treat colors as theme-baked (strip them)
    if (props._chartRebuild)
        return false;
    var raw = props.series;
    return Boolean(raw === null || raw === void 0 ? void 0 : raw.some(function (s) { return typeof s.color === 'string' && s.color.length > 0; }));
}
/** Persist series for rebuild without baking live theme colors. */
export function seriesForWidgetState(props, seriesHasUserColors) {
    var raw = props.series;
    if (!(raw === null || raw === void 0 ? void 0 : raw.length))
        return undefined;
    return raw.map(function (s) {
        var _a;
        var next = {
            name: s.name,
            label: s.label,
            data: s.data,
            type: s.type,
            yAxis: s.yAxis,
            errorY: s.errorY,
            rangeMin: s.rangeMin,
            rangeMax: s.rangeMax,
        };
        if (seriesHasUserColors && s.color)
            next.color = s.color;
        if ((_a = s.colorStops) === null || _a === void 0 ? void 0 : _a.length)
            next.colorStops = s.colorStops;
        return next;
    });
}
export function seriesPointCount(series) {
    return Math.max.apply(Math, __spreadArray(__spreadArray([], __read(series.map(function (s) { return s.data.length; })), false), [0], false));
}
export function flattenSeriesData(series) {
    return series.flatMap(function (s) { return s.data; });
}
export function stackSeries(series) {
    var _a;
    var len = seriesPointCount(series);
    var stacked = series.map(function () { return Array(len).fill(0); });
    for (var i = 0; i < len; i++) {
        var acc = 0;
        for (var s = 0; s < series.length; s++) {
            var v = (_a = series[s].data[i]) !== null && _a !== void 0 ? _a : 0;
            acc += v;
            stacked[s][i] = acc;
        }
    }
    return series.map(function (s, si) { return (__assign(__assign({}, s), { data: stacked[si], _base: si === 0 ? Array(len).fill(0) : stacked[si - 1] })); });
}
export function normalizeBumpRanks(series) {
    var len = seriesPointCount(series);
    var result = [];
    var _loop_1 = function (i) {
        var vals = series.map(function (s, si) { var _a; return ({ si: si, v: (_a = s.data[i]) !== null && _a !== void 0 ? _a : 0 }); });
        vals.sort(function (a, b) { return b.v - a.v; });
        vals.forEach(function (entry, rank) {
            if (!result[entry.si]) {
                result[entry.si] = __assign(__assign({}, series[entry.si]), { data: [] });
            }
            result[entry.si].data[i] = rank + 1;
        });
    };
    for (var i = 0; i < len; i++) {
        _loop_1(i);
    }
    return result;
}
